import { useState } from 'react'

import { type IndexProgress, rebuildIndex } from '../../api/search'
import { Button } from '../../ui/Control'
import { Done } from '../../ui/Status'
import { useAnnounce } from '../../ui/Announce'

/**
 * How far the reading of this space has got, and the way to start it again.
 *
 * Only in the detailed search. The layer over the page says the one sentence that matters there — that
 * something is still being read — and nothing else: somebody who pressed ⌘K is looking for a document,
 * not for the state of an index.
 *
 * "Read everything again" asks no question before it acts. It costs time and nothing else: the text that
 * is already there stays until it has been read afresh, so the search goes on answering throughout, and
 * the counts move while it happens.
 */
export interface ProgressProps {
  readonly progress: IndexProgress | null
  /** Told the new state, so the surrounding search shows the count moving at once. */
  readonly onRebuilt: (progress: IndexProgress) => void
}

export function Progress({ progress, onRebuilt }: ProgressProps) {
  const [asked, setAsked] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const announce = useAnnounce()

  if (progress === null || progress.total === 0) {
    return null
  }

  return (
    <div className="progress">
      <p className="progress__what">
        <b>
          {progress.indexed} of {progress.total}
        </b>{' '}
        read
        {progress.withoutText > 0 && `, ${progress.withoutText} with no text in them`}
        {progress.failed > 0 && `, ${progress.failed} unreadable`}
        {progress.outstanding > 0 && `, ${progress.outstanding} still to go`}
      </p>
      <div className="controls controls--tight">
        <Button
          tone="bare"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            rebuildIndex()
              .then((now) => {
                onRebuilt(now)
                setAsked(Date.now())
                announce('Everything will be read again.')
              })
              .catch(() => {
                announce('That did not work. Try again in a moment.')
              })
              .finally(() => {
                setBusy(false)
              })
          }}
        >
          Read everything again
        </Button>
        {/* Keyed by when it was asked for, so a second attempt shows a second confirmation. */}
        {asked !== null && <Done key={asked} label="Started" />}
      </div>
    </div>
  )
}
